import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import config from '../../config'
import { useStoreContext } from '../../contexts/store-context'
import { SessionDetails, StoreEvent } from '../../types/SessionDetails'
import { dateStringToDisplayDate, differenceInMinutes, getLocalizedDateTimeString, getSessionDateTimeSpan } from '../../utils/dateUtils'
import { get, post, remove } from '../../utils/http'
import SpinnerIcon from '../Icons/SpinnerIcon'
import CountryFlag from '../CountryFlag/CountryFlag'
import SessionTags from '../SessionTags/SessionTags'
import { getSessionSummary } from '../../utils/sessionUtils'
import { IcaCardRow } from '../IcaElements/IcaCardRow'
import { IcaPlaceholder } from '../IcaElements/IcaPlaceholder'
import '@ica-azure/ica-elements/IcaIconError'
import { getCountryCode } from '../../utils/userUtils'
import { IcaInputSwitch } from '../IcaElements/IcaInputSwitch'

export interface SessionInfoSessionDetails extends SessionDetails {
  events: SessionInfoStoreEvent[]
}

export interface SessionInfoStoreEvent extends StoreEvent {
  displayTimestamp: string
}

interface SessionInfoProps {
  onSessionFlagChanged?: (sessionId: string, isFlagged: boolean, customerId: string, countryCode: string) => void
}

export default function SessionInfo({ onSessionFlagChanged }: SessionInfoProps) {
  const { storeState } = useStoreContext()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [session, setSession] = useState<SessionInfoSessionDetails | null>(null)
  const { sessionId } = useParams()
  const [sessionFlagError, setSessionFlagError] = useState(false)

  const updateSessionEvents = useCallback((session: SessionDetails, events: StoreEvent[]) => {
    if (events == undefined || events.length < 1) {
      return
    }

    const [firstTimestamp, lastTimestamp] = [0, -1].map(i => events.at(i)?.createdAt)
    if (firstTimestamp === undefined || lastTimestamp === undefined) {
      return
    }

    let prevLocalizedDate: string | undefined = undefined
    const isSingleDaySession = firstTimestamp.substring(0, 10) === lastTimestamp.substring(0, 10)
    const sessionInfoEvents: SessionInfoStoreEvent[] = events.map(e => {
      const localized = dateStringToDisplayDate(e.createdAt)
      const localizedDate = localized.substring(0, 10)
      let displayTimestamp

      if (isSingleDaySession) { // Single day session, display no dates
        displayTimestamp = localized.substring(11, 20)
      } else { // Multi day session, display date at first event of each day
        if (prevLocalizedDate === undefined || prevLocalizedDate !== localizedDate) { // New date for session
          displayTimestamp = getLocalizedDateTimeString(e.createdAt)
          prevLocalizedDate = localizedDate
        } else {
          displayTimestamp = localized.substring(11, 20)
        }
      }

      return { ...e, displayTimestamp }
    })
    setSession({ ...session, events: sessionInfoEvents })
  }, [])

  const fetchSession = useCallback(async () => {
    try {
      const result = await get<SessionDetails>(`${config.STORE_API_BASE_URL}/v1/monitor/session/${storeState.selectedStore?.id}/${sessionId}`)
      
      const sortedEvents = result.events.sort((eventA, eventB) => {
        return new Date(eventA.createdAt).getTime() - new Date(eventB.createdAt).getTime()
      })
      setLoading(false)
      updateSessionEvents(result, sortedEvents)
    } catch {
      setError(true)
      setLoading(false)
    }
  }, [sessionId, storeState.selectedStore?.id, updateSessionEvents])

  const toggleFlagSession = async (flag: boolean) => {
    if (session === null) {
      return
    }
    
    setSessionFlagError(false)
    const newSession: SessionInfoSessionDetails = { ...session }
    try {
      if (flag) {
        const body = { SessionId: sessionId, CustomerId: session.customerId, CountryCode: getCountryCode(session.countryCode, session.acr), Acr: session.acr }
        await post(`${config.UNMANNED_API_BASE_URL}/v1/unmanned/session-flags/${storeState.selectedStore?.id}`, body)
        newSession.isSessionFlagged = true
        newSession.isCustomerFlagged = true
      } else { // Unflag
        await remove(`${config.UNMANNED_API_BASE_URL}/v1/unmanned/session-flags/${storeState.selectedStore?.id}/${sessionId}`)
        newSession.isSessionFlagged = false
      }
      setSession(newSession)
      onSessionFlagChanged?.(sessionId!, flag, session.customerId!, getCountryCode(session.countryCode, session.acr))
    } catch {
      setSessionFlagError(true)
    }
  }

  const eventTypeTranslation = {
    AbortConfirmed: 'Kundvarv avslutat',
    AbortPressedWithItemsInCart: 'Kund vill avbryta - Artiklar i varukorg',
    AbortPressedWithoutItemsInCart: 'Kund vill avbryta',
    AddToCart : 'Lägg till i varukorg',
    AgeAccessDenied: 'Identifiering (BankID)',
    CreateCart: 'Skapa varukorg',
    DoorAccessDenied: 'Öppna dörr',
    EndVendingMode: 'Avsluta påfyllning av varuautomat',
    EnterDoor : 'Öppna dörr',
    ExitDoor : 'Dörr ut',
    InitPayment : 'Start betalning',
    NotStammisAccessDenied: 'Identifiering (BankID)',
    OpenHoursAccessDenied: 'Identifiering (BankID)',
    PaymentComplete : 'Betalning genomfört',
    Purchase : 'Betalning pågår',
    PostalCodeAccessDenied: 'Identifiering (BankID)',
    RemoveFromCart : 'Ta bort ifrån varukorg',
    StartVendingMode: 'Starta påfyllning av varuautomat',
    Timeout: 'Timeout',
  }

  useEffect(() => {
    if(sessionId && storeState.selectedStore?.id) {
      fetchSession()
    }
  }, [sessionId, storeState.selectedStore?.id, fetchSession])

  return (
    <>
      {session && (
        <>
          <IcaCardRow noninteractive>
            <div className='flex flex-col flex-wrap break-all text-sm'>
              <p>{`Kund-id: ${session.customerId}`}</p>
              {session.id && <p>{`Kundvarvs-id: ${session.id}`}</p>}
              <p className='font-bold'>{`${getSessionDateTimeSpan(session.firstActivity, session.latestActivity)} (${differenceInMinutes(session.firstActivity, session.latestActivity)} min)`}</p>
            </div>
            {session.acr && <div><CountryFlag countryCode={getCountryCode(session.countryCode, session.acr)}/></div>}
          </IcaCardRow>

          <div className='bg-white flex justify-between items-center px-4 py-2'>
            <div>
              <div className='text-xl font-bold'>{getSessionSummary(session)}</div>
              {session.paymentInternalDisplayName && <div className='text-sm font-semibold'>{session.paymentInternalDisplayName}</div>}
              <div className='mt-2'>
                <SessionTags accessDeniedReason={session.events.find(e => e.eventType === 'DoorAccessDenied' 
                  || e.eventType === 'AgeAccessDenied' 
                  || e.eventType === 'NotStammisAccessDenied'
                  || e.eventType === 'OpenHoursAccessDenied'
                  || e.eventType === 'PostalCodeAccessDenied')?.eventType || undefined}
                hasCustomerWeighedItems={session.hasCustomerWeighedItems}
                hasMultipleCompletedPayments={session.hasMultipleCompletedPayments}
                hasRemovedItems={session.hasRemovedItems}
                isMissingPayment={session.isMissingPayment}
                isSessionFlagged={session.isSessionFlagged}
                isCustomerFlagged={session.isCustomerFlagged}
                >
                </SessionTags>
              </div>
            </div>
          </div>

          <div className='bg-white px-4'>
            <div className='flex justify-between items-center py-3'>
              <span>Flagga kundvarv</span>
              <IcaInputSwitch
                checked={!!session.isSessionFlagged}
                onChange={(e: ChangeEvent<HTMLInputElement>) => toggleFlagSession(!e.target.checked)}
              />
            </div>
            {sessionFlagError && <p className='text-[var(--color-accent-red)] mb-3'>Det gick inte att flagga kundvarvet</p>}
          </div>

          <IcaCardRow noninteractive>
            <div className='text-lg font-bold'>Händelser</div> 
          </IcaCardRow>
          
          {session.events.map((event) => 
            <IcaCardRow key={`${event.createdAt}-${event.eventType}`} noninteractive>
              <div>
                <div className='text-[14px]'>
                  <p className='leading-[18px]'>{eventTypeTranslation[event.eventType as keyof typeof eventTypeTranslation] || event.eventType}</p>
                  <span className='leading-4 font-bold'>{event.itemName}</span>{event.itemWeightInGrams && <span className='ml-2 leading-4'>{`Vikt: ${event.itemWeightInGrams}g`}</span>}
                </div>
                
                <div key={`${event.createdAt}-${event.eventType}`}>
                  <div className='flex'>
                    <div className='text-sm'>
                      {(event.eventType === 'DoorAccessDenied') && <div className='truncate font-bold'>Spärrad användare</div>}
                      {(event.eventType === 'AgeAccessDenied') && <div className='truncate font-bold'>Nekad användare p.g.a. ålderskrav</div>}
                      {(event.eventType === 'NotStammisAccessDenied') && <div className='truncate font-bold'>Nekad användare p.g.a. icke-stammis</div>}
                      {(event.eventType === 'OpenHoursAccessDenied') && <div className='truncate font-bold'>Nekad användare p.g.a. anpassade öppettider</div>}
                      {(event.eventType === 'PostalCodeAccessDenied') && <div className='truncate font-bold'>Nekad användare p.g.a. postnummerkrav</div>}
                      {event.eventType === 'RemoveFromCart' && <div className='mt-1'><div className='session-tags__tag inline-block'>Borttagning av vara</div></div>}
                    </div>
                  </div>
                </div>
              </div>

              <span className='text-sm text-[var(--color-gray)]'>{event.displayTimestamp}</span>
            </IcaCardRow>
          )}
        </>
      )}
      {loading &&
      <div className='grid place-content-center h-72'>
        <SpinnerIcon />
      </div>
      }
      {!loading && error && 
      <div className='my-12'>
        <IcaPlaceholder
          text='Någonting gick fel. Försök igen genom att ladda om sidan.'
          align='center'
        >
          <tgdb-ica-icon-error/>
        </IcaPlaceholder>
      </div>
      }
    
    </>
  )
}
